import { EuiToolTip } from "@elastic/eui";
import React, { useCallback, useContext } from "react";
import { useQueryClient } from "react-query";

import client from "../../api/Client";
import { parseInternalProductId } from "../../utils/FormUtils";
import { ShopContext } from "../../utils/Shop";
import { Price } from "../../utils/types";
import InlineTextEditor from "./InlineTextEditor";

interface InternalProductIdCellEditorProps {
    id: string;
    price: Price;
    currentShopGroupId: string;
    updatePriceMutation: {
        mutate: (args: { priceId: string; data: { internal_product_id: string; shop_group_id: string } }) => void;
    };
    setItems: React.Dispatch<React.SetStateAction<Array<{ id: string; internal_product_id: string }>>>;
    mode: string;
}

export const InternalProductIdCell = React.memo(
    ({ id, price, currentShopGroupId, mode }: InternalProductIdCellEditorProps) => {
        const { prefix } = parseInternalProductId(id);
        const { shopId } = useContext(ShopContext);
        const [showWarning, setShowWarning] = React.useState(false);

        const queryClient = useQueryClient();

        const onSave = useCallback(
            async (newPrefix: string) => {
                if (!/^\d+$/.test(newPrefix)) return;

                const result = await client.get(
                    `/v1/prices?skip=0&limit=9999&filter=shop_group_id:${currentShopGroupId}`
                );
                const matchedPrice = result.data.find((p: any) => p.internal_product_id.startsWith(`${newPrefix}:`));

                if (!matchedPrice) return;

                await client.put(`/v1/shops-to-prices/${price.id}`, {
                    active: price.active,
                    new: price.new,
                    price_id: matchedPrice.id,
                    shop_id: shopId,
                    category_id: price.category_id,
                    kind_id: price.kind_id,
                    deactivated_labels: price.deactivated_labels ?? [],
                    action_labels: price.action_labels ?? [],
                });

                queryClient.setQueryData(["products", shopId, mode], (oldData: any) => {
                    if (!oldData) return oldData;
                    return {
                        ...oldData,
                        prices: oldData.prices.map((p: any) =>
                            p.id === price.id
                                ? {
                                      ...p,
                                      internal_product_id: matchedPrice.internal_product_id,
                                      price_id: matchedPrice.id,
                                      json_prices: [
                                          ...(matchedPrice.cannabis ?? []),
                                          ...(matchedPrice.pre_rolled_joints ?? []),
                                          ...(matchedPrice.edible ?? []),
                                      ],
                                  }
                                : p
                        ),
                    };
                });
            },
            [price, currentShopGroupId, shopId, mode, queryClient]
        );

        return (
            <EuiToolTip
                position="right"
                content={
                    "Het bewerken van de code (kassacode) koppelt de dit product aan een bestaande Prijs Combi. Zorg ervoor dat de gewenste Prijs Combi al in het systeem bestaat voordat je deze wijzigt."
                }
            >
                <InlineTextEditor
                    name="Internal Product Code"
                    initialValue={prefix}
                    readonly={false}
                    withPencil={true}
                    onEdit={() => console.log("Editing !")}
                    onSave={onSave}
                />
            </EuiToolTip>
        );
    }
);
